import { NavLink } from 'react-router-dom'
import { motion } from 'framer-motion'
import type { ReactNode } from 'react'
import {
  HomeIcon, NeedIcon, TimelineIcon, PeopleIcon, AskIcon, SettingsIcon,
} from '@/design-system/icons'
import { useAppStore } from '@/stores/appStore'
import { useUnread, type TabKey } from '@/lib/unreadBadges'

/**
 * DesktopShell — renders a permanent left sidebar + centered main content.
 * Used only on viewports >= 1024px. Kills the "iPhone-in-a-window" look.
 *
 * Sidebar shows: Wren wordmark, primary nav (with unread badges), Search + Ask
 * + Trends secondary group, account footer. Full-height, sticky.
 */

const PRIMARY: Array<{ to: string; label: string; icon: typeof HomeIcon; unreadKey?: TabKey }> = [
  { to: '/',         label: 'Today',    icon: HomeIcon,     unreadKey: 'today' },
  { to: '/timeline', label: 'Timeline', icon: TimelineIcon, unreadKey: 'timeline' },
  { to: '/needs',    label: 'Needs',    icon: NeedIcon,     unreadKey: 'needs' },
  { to: '/family',   label: 'Family',   icon: PeopleIcon,   unreadKey: 'family' },
]

const SECONDARY = [
  { to: '/ask',      label: 'Ask + Log',  icon: AskIcon,      key: 'ask' as const },
  { to: '/search',   label: 'Search',     icon: SearchGlyph,  key: 'search' as const },
  { to: '/trends',   label: 'Trends',     icon: TrendGlyph,   key: 'trends' as const },
  { to: '/items',    label: 'Items',      icon: ItemsGlyph,   key: 'items' as const },
]

export function DesktopShell({ children }: { children: ReactNode }) {
  const openNeeds = useAppStore((s) => s.openNeedsCount)
  const user = useAppStore((s) => s.user)
  const unread = useUnread((s) => s.counts)
  const markSeen = useUnread((s) => s.markSeen)

  return (
    <div className="min-h-svh flex" style={{ background: 'rgb(var(--bg-base))' }}>
      {/* SIDEBAR — permanent left rail */}
      <aside
        className="w-64 shrink-0 border-r border-divider-subtle/40 flex flex-col sticky top-0 h-svh"
        style={{ background: 'rgb(var(--bg-elevated) / 0.55)', backdropFilter: 'blur(20px)' }}
      >
        <div className="px-6 pt-8 pb-6">
          <div
            className="font-display text-text-primary"
            style={{ fontSize: 28, fontWeight: 500, letterSpacing: '-0.02em', lineHeight: 1 }}
          >
            Wren
          </div>
          <div className="mt-1 text-tag text-text-tertiary">the family record</div>
        </div>

        <nav className="flex-1 px-3 pb-3 space-y-0.5 overflow-y-auto">
          <div className="kicker px-3 py-2">Primary</div>
          {PRIMARY.map((t) => {
            const Icon = t.icon
            const badge = t.to === '/needs' ? openNeeds : (t.unreadKey ? unread[t.unreadKey] : 0)
            const badgeTone: 'coral' | 'mint' = t.to === '/needs' ? 'coral' : 'mint'
            return (
              <NavLink
                key={t.to} to={t.to} end={t.to === '/'}
                onClick={() => { if (t.unreadKey) markSeen(t.unreadKey) }}
                className={({ isActive }) => `flex items-center gap-3 rounded-r-sm px-3 py-2.5 text-body transition-all ${isActive ? 'text-text-primary' : 'text-text-secondary hover:text-text-primary'}`}
                style={({ isActive }: { isActive: boolean }) => isActive ? { background: 'rgb(var(--bg-hover) / 0.7)' } : {}}
              >
                <span className="w-5 h-5 flex items-center justify-center text-text-tertiary"><Icon size={18} /></span>
                <span className="flex-1 truncate">{t.label}</span>
                {badge > 0 && (
                  <span
                    className="min-w-[20px] h-5 rounded-full text-[10px] font-bold flex items-center justify-center px-1.5 text-white shrink-0"
                    style={{
                      background: badgeTone === 'coral'
                        ? 'linear-gradient(180deg, #FF8942 0%, #FF7A2D 100%)'
                        : 'linear-gradient(180deg, #40DDAF 0%, #2ED2A3 100%)',
                    }}
                  >
                    {badge > 99 ? '99+' : badge}
                  </span>
                )}
              </NavLink>
            )
          })}

          <div className="kicker px-3 py-2 pt-4">Tools</div>
          {SECONDARY.map((t) => {
            const Icon = t.icon
            return (
              <NavLink
                key={t.to} to={t.to}
                className={({ isActive }) => `flex items-center gap-3 rounded-r-sm px-3 py-2.5 text-body transition-all ${isActive ? 'text-text-primary' : 'text-text-secondary hover:text-text-primary'}`}
                style={({ isActive }: { isActive: boolean }) => isActive ? { background: 'rgb(var(--bg-hover) / 0.7)' } : {}}
              >
                <span className="w-5 h-5 flex items-center justify-center text-text-tertiary"><Icon size={18} /></span>
                <span className="flex-1 truncate">{t.label}</span>
              </NavLink>
            )
          })}

          <div className="pt-4 px-3 space-y-1 text-tag text-text-quiet">
            <div>⌘K search · ⌘L voice log</div>
            <div>⌘T timeline · ⌘. settings</div>
          </div>
        </nav>

        <div className="border-t border-divider-subtle/30 p-4">
          <NavLink
            to="/settings"
            className={({ isActive }) => `flex items-center gap-3 rounded-r-sm px-3 py-2 text-caption transition-all ${isActive ? 'text-text-primary' : 'text-text-secondary hover:text-text-primary'}`}
          >
            <span className="w-8 h-8 rounded-full bg-accent-mint/25 text-accent-mint flex items-center justify-center text-caption font-semibold">
              {(user?.email ?? '?').slice(0, 1).toUpperCase()}
            </span>
            <span className="flex-1 min-w-0 truncate">{user?.email ?? '—'}</span>
            <SettingsIcon size={16} />
          </NavLink>
        </div>
      </aside>

      {/* MAIN */}
      <motion.main
        initial={{ opacity: 0, x: 6 }} animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.24 }}
        className="flex-1 min-w-0 overflow-y-auto"
      >
        <div className="mx-auto max-w-3xl w-full">
          {children}
        </div>
      </motion.main>
    </div>
  )
}

// Small inline icon glyphs (avoid new imports)
function SearchGlyph({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
    </svg>
  )
}
function TrendGlyph({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 17l6-6 4 4 7-7" /><path d="M14 8h6v6" />
    </svg>
  )
}
function ItemsGlyph({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" />
    </svg>
  )
}
